import { ActionConfirmation } from "./ui/ActionConfirmation.js";
import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { creditReviewApi, type PurchaseClaim, type PurchaseClaimDelivery } from "./creditReview.js";
import { PaymentStudentSearch } from "./pages/LogPayment.js";
import { ClaimTime, PendingPurchaseClaimCard } from "./PendingPurchaseClaimCard.js";
import { purchaseClaimState } from "./purchaseClaimState.js";
import { safePositiveId } from "./purchaseBookingLink.js";
import { formatDateTime } from "./format.js";
import { Dialog, ConfirmDialog } from "./ui/Dialog.js";
import { useAsync } from "./useAsync.js";

/** A paid claim stays held until the original Amelia booking has a student and staff activate it. */
export function PurchaseClaimQueue({ onChanged }: { onChanged: () => void }) {
  const cutover = useAsync(() => creditReviewApi.cutoverStatus(), []);
  const claims = useAsync(() => creditReviewApi.pendingPurchaseClaims(), []);
  const failedMails = useAsync(() => creditReviewApi.failedCreditMails(), []);
  const [delivery, setDelivery] = useState<{ source: PurchaseClaim[]; items: PurchaseClaimDelivery[] | null } | null>(null);
  const [resendConfirmation, setResendConfirmation] = useState<PurchaseClaim | null>(null);
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    setNow(Date.now());
    if (!claims.data?.length) return;
    // Re-evaluate send/expiry evidence while the queue remains open. This clock
    // never reloads data or dispatches mail and is shared by all cards.
    const timer = window.setInterval(() => setNow(Date.now()), 30_000);
    return () => window.clearInterval(timer);
  }, [claims.data]);
  useEffect(() => {
    if (claims.loading || !claims.data) return;
    let current = true;
    const source = claims.data;
    const paymentIds = [...new Set(source.map((block) => block.amelia_payment_id).filter(safePositiveId))];
    if (paymentIds.length === 0) {
      setDelivery({ source, items: [] });
      return;
    }
    creditReviewApi.purchaseClaimDelivery(paymentIds)
      .then(({ items }) => { if (current) setDelivery({ source, items }); })
      .catch(() => { if (current) setDelivery({ source, items: null }); });
    return () => { current = false; };
  }, [claims.data, claims.loading]);
  function deliveryFor(block: PurchaseClaim) {
    return delivery?.source === claims.data
      ? delivery.items?.find((item) => item.paymentId === block.amelia_payment_id) : undefined;
  }
  function refreshClaims() { claims.reload(); }
  function resendPending(block: PurchaseClaim) {
    return claims.loading || (safePositiveId(block.amelia_payment_id) && delivery?.source !== claims.data);
  }
  function requestResend(block: PurchaseClaim) {
    if (busy || resendInFlight.current || resendPending(block)) return;
    const state = purchaseClaimState(block, deliveryFor(block), Date.now());
    if (state.nextStep.resend !== "allowed") return;
    if (state.invitation.confirmResend) { setError(null); setResendConfirmation(block); }
    else void resend(block);
  }
  const [selected, setSelected] = useState<PurchaseClaim | null>(null);
  const [student, setStudent] = useState<{ id: string; fullName: string } | null>(null);
  const [method, setMethod] = useState<"verified_claim" | "staff_assignment">("staff_assignment");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const resendInFlight = useRef(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<string | null>(null);

  async function resend(block: PurchaseClaim) {
    if (busy || resendInFlight.current) return;
    if (resendPending(block)) {
      setError("Delivery evidence is refreshing. Wait for it to finish, then review the email status and try again. No resend was requested.");
      return;
    }
    const currentBlock = claims.data?.find((claim) => claim.id === block.id);
    if (!currentBlock) {
      setError("This purchase is no longer in the pending claims list. Close this confirmation and refresh the queue. No resend was requested.");
      return;
    }
    const state = purchaseClaimState(currentBlock, deliveryFor(currentBlock), Date.now());
    if (state.nextStep.resend !== "allowed") {
      setError(`The claim's status changed. ${state.nextStep.resendReason ?? state.nextStep.text} Close this confirmation and review the purchase. No resend was requested.`);
      return;
    }
    resendInFlight.current = true;
    setBusy(true);
    setError(null);
    setResult(null);
    try {
      await creditReviewApi.resendPurchaseClaim(block.id);
      setResendConfirmation(null);
      setResult("Invitation queued for the claim worker. The paid purchase remains held.");
      refreshClaims();
      failedMails.reload();
      onChanged();
    } catch (cause) {
      const detail = cause instanceof Error ? cause.message : "Could not queue the invitation.";
      setError(block.claim_payer_email ? detail : `${detail} Resolve the payer contact in Amelia, then check contact and send the invitation again. The purchase remains held.`);
    } finally {
      resendInFlight.current = false;
      setBusy(false);
    }
  }

  async function activate() {
    if (!selected || !student || reason.trim().length < 3 || busy) return;
    setBusy(true);
    setError(null);
    try {
      await creditReviewApi.activatePurchaseClaim(selected.id, student.id, method, reason.trim());
      setResult(`Purchase assigned to ${student.fullName}. The Hub access worker will run next.`);
      setSelected(null);
      refreshClaims();
      failedMails.reload();
      onChanged();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Could not activate the purchase.");
    } finally {
      setBusy(false);
    }
  }

  return <section aria-label="Purchase access and delivery">
    <section className="panel" aria-label="Credit-only cutover status">
      <h3>Credit-only cutover</h3>
      <button type="button" className="quiet" onClick={() => { cutover.reload(); failedMails.reload(); claims.reload(); }}>Refresh cutover status</button>
      {cutover.loading && <p role="status">Checking the credit cutover…</p>}
      {cutover.error && <p className="error" role="alert">Could not check the credit cutover: {cutover.error}</p>}
      {cutover.data && <>
        <p className="small"><strong>Future purchase cohort:</strong> {cutover.data.cohort_state}
          {cutover.data.cohort_start_at && <> · Started {formatDateTime(cutover.data.cohort_start_at)}</>}
          {cutover.data.cohort_stop_at && <> · Stops {formatDateTime(cutover.data.cohort_stop_at)}</>}
        </p>
        {cutover.data.cohort_state === "stopped" && <p className="small">The stop setting affects future purchases. Existing local credits stay local.</p>}
        <div className="small" aria-label="Credit cutover counts">
          <p>Local-cohort purchases: {cutover.data.counts.local_purchase_cohort_blocks} · Coupon-authority purchases: {cutover.data.counts.coupon_authority_purchase_blocks} · Converted coupons: {cutover.data.counts.legacy_coupon_conversions}</p>
          <p>Purchases awaiting a student: {cutover.data.counts.purchase_identity_holds} · Claims required: {cutover.data.counts.claim_required_purchases} · Legacy coupon-era holds: {cutover.data.counts.legacy_provenance_holds ?? "not reported"} · Migration holds: {cutover.data.counts.migration_held_blocks} · Conversion reviews: {cutover.data.counts.legacy_conversion_reviews}</p>
          <p>Local credit reservations: {cutover.data.counts.local_credit_reservations} · Booking reconciliation: {cutover.data.counts.booking_reconciliation_cases} · Cancellation reviews: {cutover.data.counts.cancellation_restore_reviews}</p>
          <p>Refund and chargeback reviews: {cutover.data.counts.refund_chargeback_reviews} · Payment reversal holds: {cutover.data.counts.payment_reversal_holds} · Stale workers: {cutover.data.counts.stale_workers} · Failed workers: {cutover.data.counts.failed_workers}</p>
          <p>Claim invitation errors: {cutover.data.counts.claim_invite_failures} · Credit-ready delivery reviews: {cutover.data.counts.credit_ready_delivery_failures} · Failed Hub credit emails: {failedMails.data?.total ?? "checking"}</p>
        </div>
      </>}
    </section>
    <h3>Credit email delivery</h3>
    {failedMails.loading && <p role="status">Checking credit email delivery…</p>}
    {failedMails.error && <p className="error" role="alert">Could not check credit email delivery: {failedMails.error}</p>}
    {(failedMails.data?.items ?? []).map((mail) => <div className="slot operations-row" key={mail.id}>
      <div>
        <strong>{mail.template === "hub-invitation" ? "Hub invitation" : mail.template === "purchase-claim" ? "Purchase claim email" : mail.template === "purchase-claim-action" ? "Choose-learner email" : "Lesson block ready email"} {mail.status}</strong>
        <div className="small muted">Recipient: {mail.toEmail}</div>
        <div className="small">The purchase and credits are unchanged by this mail failure. Check the contact address and email provider. Do not recreate the purchase or grant another credit.</div>
        {mail.template === "hub-invitation" && mail.studentId
          ? <div className="small"><Link to={`/students/${encodeURIComponent(mail.studentId)}`}>Open student to review Hub access and resend</Link></div>
          : (mail.template === "purchase-claim" || mail.template === "purchase-claim-action")
            ? <div className="small">Review the pending claim below before requesting another invitation.</div>
            : <div className="small">Contact the family through a verified channel if delivery cannot be recovered.</div>}
      </div>
    </div>)}
    {(failedMails.data?.total ?? 0) > 100 && <p className="small muted">Showing the oldest 100 of {failedMails.data?.total} failed messages. Review email delivery before continuing.</p>}
    <h3>Pending purchase claims</h3>
    <p className="small muted">These purchases have a pending claim. Other purchases needing a student are listed in the credit reviews below. Match each original booking before activation. Do not sell the block again.</p>
    {result && <ActionConfirmation variant="success" title="Purchase claim updated" description={result} reveal />}
    {error && !selected && <p className="error" role="alert">{error}</p>}
    {claims.loading && <p role="status">Loading pending purchase claims…</p>}
    {claims.error && <p className="error" role="alert">{claims.error}</p>}
    {!claims.loading && !claims.error && claims.data?.length === 0 && <p className="empty">No pending purchase claims. Check the credit reviews below for purchases awaiting a student.</p>}
    {(claims.data ?? []).map((block) => <PendingPurchaseClaimCard key={block.id} block={block} delivery={deliveryFor(block)} busy={busy} resendPending={resendPending(block)} now={now}
      onResend={() => requestResend(block)} onRefresh={refreshClaims} onAssign={() => {
        setSelected(block);
        setStudent(null);
        setMethod(block.claim_invite_status === "accepted" ? "verified_claim" : "staff_assignment");
        setReason("");
        setError(null);
      }} />)}
    {(claims.data?.length ?? 0) === 100 && <p className="small muted">Showing the oldest 100 claims. Work through them and refresh for the next group.</p>}

    {resendConfirmation && <ConfirmDialog title="Replace the current invitation link?"
      description={`This replaces the current link; the earlier email's link will stop working. Last email: ${purchaseClaimState(resendConfirmation, deliveryFor(resendConfirmation), Date.now()).invitation.lastEmail}.`}
      confirmLabel="Replace link and resend" confirmingLabel="Requesting…" busy={busy}
      onClose={() => { if (!busy) { setResendConfirmation(null); setError(null); } }} onConfirm={() => resend(resendConfirmation)}>
      {error && <p role="alert" className="error">{error}</p>}
    </ConfirmDialog>}

    {selected && <Dialog className="purchase-claim-assignment" title={<>Assign this paid purchase<br /><span className="small">Paid <ClaimTime value={selected.paid_at} /> · Amelia appointment {safePositiveId(selected.amelia_purchase_appointment_id) ? selected.amelia_purchase_appointment_id : "Unknown"}</span></>} description="WMF verifies the original paid Amelia purchase and its Hub booking attribution before releasing the credits. This does not take another payment." onClose={() => { if (!busy) setSelected(null); }} size="medium" busy={busy}>
      <p>First match the original booking in <Link to="/waiting?queue=unmatched">Unmatched</Link>. Choose the same student here.</p>
      {student ? <p>Student: <strong>{student.fullName}</strong> <button type="button" className="quiet" disabled={busy} onClick={() => setStudent(null)}>Change</button></p>
        : <PaymentStudentSearch ownOnly={false} onSelect={(choice) => setStudent({ id: choice.id, fullName: choice.fullName })} />}
      <label>Assignment evidence
        <select value={method} onChange={(event) => setMethod(event.target.value as typeof method)} disabled={busy}>
          <option value="staff_assignment">Staff verified booking assignment</option>
          <option value="verified_claim">Payer verified claim and approved family link</option>
        </select>
      </label>
      <label>Reason for assignment
        <textarea value={reason} onChange={(event) => setReason(event.target.value)} minLength={3} maxLength={2000} rows={3} disabled={busy} />
      </label>
      {error && <p className="error" role="alert">{error} Check the booking attribution and payment before trying again.</p>}
      <div className="ui-confirm-actions">
        <button type="button" className="action-secondary" disabled={busy} onClick={() => setSelected(null)}>Cancel</button>
        <button type="button" className="action-primary" disabled={busy || !student || reason.trim().length < 3} onClick={() => void activate()}>{busy ? "Verifying…" : "Verify and release credits"}</button>
      </div>
    </Dialog>}
  </section>;
}
